import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  ArrowRight,
  BadgeCheck,
  Building2,
  CalendarDays,
  ChevronDown,
  Clock,
  Factory,
  Gem,
  GraduationCap,
  HeartHandshake,
  HeartPulse,
  Home,
  Hotel,
  Layers,
  Mail,
  MapPin,
  MessagesSquare,
  PenLine,
  Phone,
  PhoneCall,
  Rocket,
  ShieldCheck,
  SlidersHorizontal,
  Store,
  Workflow,
} from "lucide-react";
import { useSite } from "@/context/SiteContext";
import { Reveal } from "@/components/Reveal";
import { Marquee } from "@/components/Marquee";
import { SectionHeading } from "@/components/SectionHeading";
import { ServiceCard } from "@/components/ServiceCard";
import { EnquiryForm } from "@/components/EnquiryForm";
import { scrollToId, DEFAULT_HERO_IMAGE } from "@/lib/icons";

const MaskedLine = ({ children, delay, className = "" }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className={`block ${className}`}
      initial={{ y: "112%" }}
      animate={{ y: 0 }}
      transition={{ duration: 0.95, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const VALUES = [
  { icon: BadgeCheck, label: "Professional Excellence" },
  { icon: ShieldCheck, label: "Reliability & Trust" },
  { icon: HeartHandshake, label: "Customer Satisfaction" },
];

const BENEFITS = [
  { icon: Layers, title: "Multiple Services Under One Roof", desc: "Nine professional service verticals coordinated through a single, accountable partner." },
  { icon: BadgeCheck, title: "Professional & Reliable Support", desc: "Trained teams, supervised delivery, and processes you can depend on every day." },
  { icon: HeartHandshake, title: "Customer-Focused Approach", desc: "Every engagement begins with listening — solutions shaped around your real needs." },
  { icon: SlidersHorizontal, title: "Flexible Service Solutions", desc: "Scale services up or down, or combine verticals, as your requirements evolve." },
  { icon: Workflow, title: "Efficient Coordination", desc: "One point of contact, clear communication, and on-time execution across services." },
  { icon: Gem, title: "Commitment to Quality", desc: "Consistent standards and continuous improvement across every engagement." },
];

const INDUSTRIES = [
  { icon: Building2, label: "Corporate Offices" },
  { icon: HeartPulse, label: "Hospitals & Healthcare Institutions" },
  { icon: Home, label: "Residential Communities" },
  { icon: Store, label: "Commercial Properties" },
  { icon: GraduationCap, label: "Educational Institutions" },
  { icon: CalendarDays, label: "Event & Exhibition Venues" },
  { icon: Factory, label: "Industrial & Business Facilities" },
  { icon: Hotel, label: "Retail & Hospitality Establishments" },
];

const STEPS = [
  { icon: MessagesSquare, num: "01", title: "Understand Your Requirements", desc: "We listen to your needs and identify the right service solution." },
  { icon: PenLine, num: "02", title: "Develop a Suitable Plan", desc: "We propose an approach tailored to your objectives and requirements." },
  { icon: Rocket, num: "03", title: "Deliver the Service", desc: "Our team coordinates the agreed services professionally and efficiently." },
  { icon: PhoneCall, num: "04", title: "Maintain Communication", desc: "We encourage feedback and ongoing communication to support customer satisfaction." },
];

const Hero = ({ content }) => {
  const { scrollY } = useScroll();
  const bgY = useTransform(scrollY, [0, 800], [0, 180]);
  const fade = useTransform(scrollY, [0, 500], [1, 0.3]);

  return (
    <section className="grain-overlay relative flex min-h-screen items-center overflow-hidden bg-navy-950" data-testid="hero-section">
      <motion.div style={{ y: bgY }} className="absolute inset-0 scale-110">
        <img
          src={content?.hero_image || DEFAULT_HERO_IMAGE}
          alt="Corporate professionals delivering integrated business services"
          className="h-full w-full object-cover"
        />
        <div className="hero-scrim absolute inset-0" />
        <div className="absolute inset-0 bg-navy-950/50 lg:bg-transparent" />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="relative z-10 mx-auto w-full max-w-7xl px-6 pb-28 pt-36 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-8 inline-flex items-center gap-2.5 rounded-full border border-gold/30 bg-white/5 px-4 py-2 backdrop-blur-sm"
        >
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-gold opacity-75" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-gold" />
          </span>
          <span className="font-mono text-[11px] font-semibold uppercase tracking-[0.25em] text-gold-light">
            SSS GROUPS · Multi-Disciplinary Enterprise Services
          </span>
        </motion.div>

        <h1 className="max-w-4xl font-serif text-4xl font-bold leading-[1.06] tracking-tight text-white sm:text-6xl lg:text-7xl">
          <MaskedLine delay={0.15}>Integrated Business</MaskedLine>
          <MaskedLine delay={0.28}>Solutions.</MaskedLine>
          <MaskedLine delay={0.41} className="gold-text-gradient italic">
            One Trusted Partner.
          </MaskedLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="mt-7 max-w-xl text-base leading-relaxed text-slate-300 sm:text-lg"
        >
          SIREE SOLUTIONS GROUPS delivers reliable, professional, and integrated services — across consulting,
          healthcare, property management, events, facilities, housekeeping, security, administration, and
          manpower solutions.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.75 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <Link
            to="/services"
            data-testid="hero-explore-services-btn"
            className="group flex items-center gap-2 rounded-full bg-gold px-7 py-3.5 text-sm font-bold uppercase tracking-wider text-navy-950 transition-all duration-300 hover:bg-gold-light hover:shadow-xl hover:shadow-gold/25"
          >
            Explore Our Services
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
          <Link
            to="/contact"
            data-testid="hero-contact-btn"
            className="rounded-full border border-gold/50 px-7 py-3.5 text-sm font-bold uppercase tracking-wider text-gold transition-all duration-300 hover:bg-gold/10"
          >
            Contact Us
          </Link>
        </motion.div>
      </motion.div>

      <motion.button
        onClick={() => scrollToId("about-preview")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4 }}
        className="absolute bottom-8 left-1/2 z-10 -translate-x-1/2 text-gold/70 transition-colors hover:text-gold"
        aria-label="Scroll to about section"
        data-testid="hero-scroll-cue"
      >
        <ChevronDown className="h-7 w-7 animate-bounce" />
      </motion.button>
    </section>
  );
};

const HomePage = () => {
  const { content } = useSite();
  const services = content?.services || [];
  const storyFirst = (content?.story || "").split("\n\n")[0];

  useEffect(() => {
    if (window.location.hash) {
      const id = window.location.hash.slice(1);
      setTimeout(() => scrollToId(id), 700);
    }
  }, []);

  return (
    <div data-testid="home-page">
      <Hero content={content} />
      <Marquee />

      {/* About preview */}
      <section id="about-preview" className="bg-white py-24 lg:py-32" data-testid="about-preview-section">
        <div className="mx-auto grid max-w-7xl items-center gap-16 px-6 lg:grid-cols-2 lg:px-10">
          <Reveal>
            <p className="mb-4 font-mono text-xs font-semibold uppercase tracking-[0.25em] text-gold-dark">
              01 — Who We Are
            </p>
            <h2 className="font-serif text-3xl font-bold leading-tight tracking-tight text-navy-900 sm:text-4xl lg:text-[2.75rem]">
              Your Trusted Partner for Comprehensive Business Solutions
            </h2>
            <p className="mt-6 leading-relaxed text-slate-600">
              {content?.about_short ||
                "SIREE SOLUTIONS GROUPS (SSS GROUPS) is committed to providing dependable, efficient, and customer-focused solutions across multiple service sectors."}
            </p>
            {storyFirst && (
              <blockquote className="mt-6 border-l-2 border-gold pl-5 font-serif text-lg italic leading-relaxed text-navy-800">
                {storyFirst}
                <Link to="/about#story" className="mt-2 block font-sans text-sm font-semibold not-italic text-gold-dark hover:text-gold" data-testid="about-story-link">
                  Read our full story →
                </Link>
              </blockquote>
            )}
            <div className="mt-8 flex flex-wrap gap-3">
              {VALUES.map(({ icon: Icon, label }) => (
                <span
                  key={label}
                  className="flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-4 py-2 text-sm font-semibold text-navy-800"
                >
                  <Icon className="h-4 w-4 text-gold-dark" />
                  {label}
                </span>
              ))}
            </div>
            <Link
              to="/about"
              data-testid="about-learn-more-btn"
              className="group mt-9 inline-flex items-center gap-2 rounded-full border-2 border-navy-900 px-7 py-3 text-sm font-bold uppercase tracking-wider text-navy-900 transition-all duration-300 hover:bg-navy-900 hover:text-gold"
            >
              Learn More About Us
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>

          <Reveal delay={0.15}>
            <div className="relative">
              <div className="gold-frame">
                <img
                  src={content?.about_image_1 || DEFAULT_HERO_IMAGE}
                  alt="SSS GROUPS team coordinating professional services"
                  className="w-full rounded-2xl object-cover shadow-2xl"
                  loading="lazy"
                />
              </div>
              {content?.about_image_2 && (
                <img
                  src={content.about_image_2}
                  alt="Corporate property managed by SSS GROUPS"
                  className="animate-float-slow absolute -bottom-8 -left-4 hidden w-44 rounded-xl border-4 border-white object-cover shadow-xl sm:block md:w-56"
                  loading="lazy"
                />
              )}
            </div>
          </Reveal>
        </div>
      </section>

      {/* Services */}
      <section id="services" className="bg-slate-100 py-24 lg:py-32" data-testid="services-section">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <SectionHeading
            eyebrow="02 — What We Do"
            title="Our Comprehensive Services"
            description="Discover a complete range of professional services designed to meet the diverse needs of businesses, institutions, property owners, and individuals."
          />
          {services.length > 0 ? (
            <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
              {services.map((s, i) => (
                <ServiceCard key={s.slug} service={s} index={i} />
              ))}
            </div>
          ) : (
            <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
              {Array.from({ length: 9 }).map((_, i) => (
                <div key={i} className="h-64 animate-pulse rounded-2xl bg-slate-200" />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Why choose us */}
      <section className="grain-overlay relative bg-navy-900 py-24 lg:py-32" data-testid="why-choose-section">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <SectionHeading
            eyebrow="03 — The SSS Advantage"
            title="Why Choose SIREE SOLUTIONS GROUPS?"
            dark
          />
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {BENEFITS.map(({ icon: Icon, title, desc }, i) => (
              <Reveal key={title} delay={(i % 3) * 0.08}>
                <div
                  className="group h-full rounded-2xl border border-white/10 bg-white/5 p-8 backdrop-blur-sm transition-all duration-300 hover:-translate-y-1 hover:border-gold/50 hover:bg-white/10"
                  data-testid={`benefit-card-${i + 1}`}
                >
                  <Icon className="h-8 w-8 text-gold transition-transform duration-300 group-hover:scale-110" />
                  <h3 className="mt-5 text-lg font-bold text-white">{title}</h3>
                  <p className="mt-2.5 text-sm leading-relaxed text-slate-400">{desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Industries */}
      <section id="industries" className="bg-white py-24 lg:py-32" data-testid="industries-section">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <SectionHeading
            eyebrow="04 — Who We Serve"
            title="Industries We Serve"
            description="From corporate campuses to residential communities, our teams adapt to the standards and rhythms of every environment."
          />
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {INDUSTRIES.map(({ icon: Icon, label }, i) => (
              <Reveal key={label} delay={(i % 4) * 0.06}>
                <div
                  className="group flex h-full items-center gap-4 rounded-2xl border border-slate-200 bg-slate-50 p-5 transition-all duration-300 hover:-translate-y-1 hover:border-gold hover:bg-white hover:shadow-lg hover:shadow-gold/10"
                  data-testid={`industry-tile-${i + 1}`}
                >
                  <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-navy-900 text-gold transition-colors duration-300 group-hover:bg-gold group-hover:text-navy-950">
                    <Icon className="h-6 w-6" />
                  </span>
                  <span className="text-sm font-semibold leading-snug text-navy-900">{label}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="bg-slate-100 py-24 lg:py-32" data-testid="process-section">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <SectionHeading eyebrow="05 — How We Work" title="Our Working Process" />
          <div className="grid gap-10 md:grid-cols-2 lg:grid-cols-4">
            {STEPS.map(({ icon: Icon, num, title, desc }, i) => (
              <Reveal key={num} delay={i * 0.1}>
                <div className="relative border-t-2 border-gold pt-7" data-testid={`process-step-${i + 1}`}>
                  <span className="absolute -top-[2px] left-0 h-[2px] w-10 bg-gold-dark" aria-hidden="true" />
                  <div className="flex items-baseline justify-between">
                    <span className="font-serif text-5xl font-bold text-navy-900/15">{num}</span>
                    <Icon className="h-7 w-7 text-gold-dark" />
                  </div>
                  <h3 className="mt-4 text-lg font-bold text-navy-900">{title}</h3>
                  <p className="mt-2.5 text-sm leading-relaxed text-slate-600">{desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="grain-overlay relative overflow-hidden bg-navy-950 py-24" data-testid="cta-section">
        <div className="pointer-events-none absolute left-1/2 top-1/2 h-96 w-96 -translate-x-1/2 -translate-y-1/2 rounded-full bg-gold/10 blur-3xl" />
        <Reveal className="relative mx-auto max-w-3xl px-6 text-center">
          <h2 className="font-serif text-3xl font-bold leading-tight text-white sm:text-4xl lg:text-5xl">
            Looking for a <span className="gold-text-gradient italic">Reliable Service Partner?</span>
          </h2>
          <p className="mt-5 text-base text-slate-300 sm:text-lg">
            Let SSS GROUPS help you find the right solutions for your business, property, or organization.
          </p>
          <div className="mt-9 flex flex-wrap justify-center gap-4">
            <Link
              to="/contact?quote=1"
              data-testid="cta-request-quote-btn"
              className="group flex items-center gap-2 rounded-full bg-gold px-8 py-3.5 text-sm font-bold uppercase tracking-wider text-navy-950 transition-all duration-300 hover:bg-gold-light hover:shadow-xl hover:shadow-gold/25"
            >
              Request a Quote
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
            <Link
              to="/contact"
              data-testid="cta-contact-btn"
              className="rounded-full border border-white/30 px-8 py-3.5 text-sm font-bold uppercase tracking-wider text-white transition-all duration-300 hover:border-gold hover:text-gold"
            >
              Contact Our Team
            </Link>
          </div>
        </Reveal>
      </section>

      {/* Contact */}
      <section id="contact" className="bg-white py-24 lg:py-32" data-testid="contact-section">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <SectionHeading
            eyebrow="06 — Get In Touch"
            title="Let's Talk About Your Requirements"
            description="Share a few details and our team will get back to you with the right service solution."
          />
          <div className="grid gap-10 lg:grid-cols-5">
            <Reveal className="lg:col-span-2">
              <div className="grain-overlay relative h-full overflow-hidden rounded-2xl bg-navy-900 p-9">
                <h3 className="font-serif text-2xl font-bold text-white">SIREE SOLUTIONS GROUPS</h3>
                <p className="mt-1 font-mono text-xs font-semibold uppercase tracking-[0.3em] text-gold">SSS GROUPS</p>
                <ul className="mt-8 space-y-6 text-sm">
                  <li className="flex items-start gap-4">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-gold/30 bg-gold/10 text-gold">
                      <Phone className="h-4 w-4" />
                    </span>
                    <div>
                      <p className="text-xs uppercase tracking-wider text-slate-400">Phone</p>
                      <a href={`tel:${(content?.phone || "").replace(/\s/g, "")}`} className="mt-0.5 block font-semibold text-white hover:text-gold" data-testid="contact-phone-link">
                        {content?.phone || "+91 90000 00000"}
                      </a>
                    </div>
                  </li>
                  <li className="flex items-start gap-4">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-gold/30 bg-gold/10 text-gold">
                      <MessagesSquare className="h-4 w-4" />
                    </span>
                    <div>
                      <p className="text-xs uppercase tracking-wider text-slate-400">WhatsApp</p>
                      <a
                        href={`https://wa.me/${(content?.whatsapp || "919000000000").replace(/[^0-9]/g, "")}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-0.5 block font-semibold text-white hover:text-gold"
                        data-testid="contact-whatsapp-link"
                      >
                        {content?.whatsapp || "+91 90000 00000"}
                      </a>
                    </div>
                  </li>
                  <li className="flex items-start gap-4">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-gold/30 bg-gold/10 text-gold">
                      <Mail className="h-4 w-4" />
                    </span>
                    <div>
                      <p className="text-xs uppercase tracking-wider text-slate-400">Email</p>
                      <a href={`mailto:${content?.email || "info@sssgroups.com"}`} className="mt-0.5 block font-semibold text-white hover:text-gold" data-testid="contact-email-link">
                        {content?.email || "info@sssgroups.com"}
                      </a>
                    </div>
                  </li>
                  <li className="flex items-start gap-4">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-gold/30 bg-gold/10 text-gold">
                      <MapPin className="h-4 w-4" />
                    </span>
                    <div>
                      <p className="text-xs uppercase tracking-wider text-slate-400">Office</p>
                      <p className="mt-0.5 font-semibold text-white">{content?.address || "Office address — to be updated"}</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-4">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-gold/30 bg-gold/10 text-gold">
                      <Clock className="h-4 w-4" />
                    </span>
                    <div>
                      <p className="text-xs uppercase tracking-wider text-slate-400">Business Hours</p>
                      <p className="mt-0.5 font-semibold text-white">{content?.hours || "Monday – Saturday: 9:00 AM – 6:00 PM"}</p>
                    </div>
                  </li>
                </ul>
              </div>
            </Reveal>
            <Reveal delay={0.12} className="lg:col-span-3">
              <div className="h-full rounded-2xl border border-slate-200 bg-white p-8 shadow-xl shadow-slate-200/60 sm:p-10">
                <h3 className="font-serif text-2xl font-bold text-navy-900">Send Us an Enquiry</h3>
                <p className="mb-8 mt-2 text-sm text-slate-500">
                  Fields marked <span className="text-gold-dark">*</span> are required. We typically respond within one business day.
                </p>
                <EnquiryForm />
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
